import { Navigate, Outlet } from 'react-router';
import RouteGuardWrapper from './RouteGuardWrapper';
import SkinFeatureGuard from './SkinFeatureGuard';
import { LoaderContainer } from '../components/atoms/LoaderContainer';
import { SKIN_FEATURE } from '../config/skinRegistry/configs';
import { FEATURE_FLAG } from '../constants/featureFlags';
import useProductAvailability from '../hooks/useProductAvailability';
import { lazyWithRetry } from '../utils/lazyWithRetry';
import {
  HomePage,
  SportsPage,
  ContentPage,
  SportPage,
  TournamentPage,
  TournamentMatchesPage,
  TournamentOutrightsPage,
  SearchPage,
  EventPage,
  UpcomingMatchesPage,
  MyBetsPageWrapper,
  InPlayPage,
  InPlayEventPage,
  AccountBalancePage,
  MyAccountPage,
  PreferencesPage,
  SecurityPage,
  BonusPage,
  MessagesPage,
  CurrentBalancePage,
  ChangePasswordPage,
  BettingHistoryPage,
  CasinoHistoryPage,
  CasinoPage,
  CasinoGamePage,
  StandaloneCasinoPage,
  TransactionsHistoryPage,
  PromotionsPage,
  PromotionDetailsPage,
} from '../config/skinRegistry';

const InPlayFavoritesPage = lazyWithRetry(() => import('../components/pages/InPlayFavoritesPage'));
const ProfilePage = lazyWithRetry(() => import('../components/pages/ProfilePage'));
const ResetPasswordPage = lazyWithRetry(() => import('../components/pages/ResetPasswordPage'));
const UserListPage = lazyWithRetry(() => import('../components/pages/UserListPage'));
const UserDetailsPage = lazyWithRetry(() => import('../components/pages/UserDetailsPage'));

const commonRoutes = [
  {
    path: '/reset-password/:token',
    element: (
      <RouteGuardWrapper>
        <ResetPasswordPage />
      </RouteGuardWrapper>
    ),
  },
];

const ProductAwareHistoryRedirect = () => {
  const { hasSportsbookAccess, isProductAvailabilityResolved } = useProductAvailability();

  if (!isProductAvailabilityResolved) {
    return <LoaderContainer />;
  }

  return <Navigate to={hasSportsbookAccess ? 'sports-betting' : 'casino'} replace />;
};

const ProductAwareBonusRedirect = () => {
  const { hasSportsbookAccess, isProductAvailabilityResolved } = useProductAvailability();

  if (!isProductAvailabilityResolved) {
    return <LoaderContainer />;
  }

  return <Navigate to={hasSportsbookAccess ? 'sports' : 'casino'} replace />;
};

const sportsbookRoutes = [
  {
    index: true,
    element: <HomePage />,
  },
  {
    path: '/sports',
    element: (
      <RouteGuardWrapper sportsbookAccess>
        <Outlet />
      </RouteGuardWrapper>
    ),
    children: [
      {
        index: true,
        element: (
          <RouteGuardWrapper miniStats sportsbookAccess>
            <SportsPage />
          </RouteGuardWrapper>
        ),
      },
      {
        path: ':sportId',
        children: [
          {
            index: true,
            element: <Navigate to='matches' replace />,
          },
          {
            path: ':section',
            element: (
              <RouteGuardWrapper sportsbookAccess>
                <SportPage />
              </RouteGuardWrapper>
            ),
          },
          {
            path: 'upcoming-matches',
            children: [
              {
                index: true,
                element: <Navigate to='top-leagues' replace />,
              },
              {
                path: ':tab',
                element: (
                  <RouteGuardWrapper sportsbookAccess>
                    <UpcomingMatchesPage />
                  </RouteGuardWrapper>
                ),
              },
            ],
          },
          {
            path: 'tournaments/:tournamentId',
            element: (
              <RouteGuardWrapper sportsbookAccess>
                <TournamentPage />
              </RouteGuardWrapper>
            ),
            children: [
              {
                index: true,
                element: <Navigate to='matches' replace />,
              },
              {
                path: 'matches',
                element: (
                  <RouteGuardWrapper sportsbookAccess>
                    <TournamentMatchesPage />
                  </RouteGuardWrapper>
                ),
              },
              {
                path: 'outrights',
                element: (
                  <RouteGuardWrapper sportsbookAccess>
                    <TournamentOutrightsPage />
                  </RouteGuardWrapper>
                ),
              },
            ],
          },
        ],
      },

      {
        path: 'events/:eventId',
        children: [
          {
            index: true,
            element: (
              <RouteGuardWrapper sportsbookAccess>
                <EventPage />
              </RouteGuardWrapper>
            ),
          },
          {
            path: ':marketGroupId',
            element: (
              <RouteGuardWrapper sportsbookAccess>
                <EventPage />
              </RouteGuardWrapper>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '/in-play',
    element: (
      <RouteGuardWrapper miniStats sportsbookAccess>
        <Outlet />
      </RouteGuardWrapper>
    ),
    children: [
      { index: true, element: <InPlayPage /> },
      { path: 'sport/:sportId', element: <InPlayPage /> },
      {
        path: 'favorites',
        element: (
          <RouteGuardWrapper sportsbookAccess>
            <SkinFeatureGuard feature={SKIN_FEATURE.FAVORITES} redirect='/in-play'>
              <InPlayFavoritesPage />
            </SkinFeatureGuard>
          </RouteGuardWrapper>
        ),
      },
    ],
  },
  {
    path: '/in-play/match-view/:eventId',
    element: (
      <RouteGuardWrapper sportsbookAccess>
        <Outlet />
      </RouteGuardWrapper>
    ),
    children: [
      {
        index: true,
        element: (
          <RouteGuardWrapper sportsbookAccess>
            <InPlayEventPage />
          </RouteGuardWrapper>
        ),
      },
      {
        path: ':marketGroupId',
        element: (
          <RouteGuardWrapper sportsbookAccess>
            <InPlayEventPage />
          </RouteGuardWrapper>
        ),
      },
    ],
  },
  {
    path: '/search',
    element: (
      <RouteGuardWrapper maintenance={false}>
        <SearchPage />
      </RouteGuardWrapper>
    ),
  },

  {
    path: '/my-bets',
    element: (
      <RouteGuardWrapper auth sportsbookAccess>
        <MyBetsPageWrapper />
      </RouteGuardWrapper>
    ),
  },
  {
    path: '/current-balance',
    element: (
      <RouteGuardWrapper auth>
        <CurrentBalancePage />
      </RouteGuardWrapper>
    ),
  },
  {
    path: '/account-balance',
    element: (
      <RouteGuardWrapper auth>
        <AccountBalancePage />
      </RouteGuardWrapper>
    ),
  },
  {
    path: '/user-list',
    element: (
      <RouteGuardWrapper auth>
        <UserListPage />
      </RouteGuardWrapper>
    ),
  },
  {
    path: '/user-details/:userId',
    element: (
      <RouteGuardWrapper auth>
        <UserDetailsPage />
      </RouteGuardWrapper>
    ),
  },
  {
    path: '/my-account',
    children: [
      {
        index: true,
        element: (
          <RouteGuardWrapper auth>
            <MyAccountPage />
          </RouteGuardWrapper>
        ),
      },
      {
        path: 'history',
        children: [
          {
            index: true,
            element: <ProductAwareHistoryRedirect />,
          },
          {
            path: 'sports-betting',
            element: (
              <RouteGuardWrapper auth sportsbookAccess>
                <BettingHistoryPage />
              </RouteGuardWrapper>
            ),
          },
          {
            path: 'casino',
            element: (
              <RouteGuardWrapper auth casinoAccess maintenanceProduct='casino'>
                <CasinoHistoryPage />
              </RouteGuardWrapper>
            ),
          },
          {
            path: 'transactions',
            element: (
              <RouteGuardWrapper auth maintenance={false}>
                <TransactionsHistoryPage />
              </RouteGuardWrapper>
            ),
          },
        ],
      },
      {
        path: 'bonus',
        children: [
          {
            index: true,
            element: <ProductAwareBonusRedirect />,
          },
          {
            path: 'sports',
            element: (
              <RouteGuardWrapper sportsbookAccess>
                <BonusPage />
              </RouteGuardWrapper>
            ),
          },
          {
            path: 'casino',
            element: (
              <RouteGuardWrapper casinoAccess maintenanceProduct='casino'>
                <BonusPage />
              </RouteGuardWrapper>
            ),
          },
        ],
      },
      {
        path: 'preferences',
        element: (
          <RouteGuardWrapper auth>
            <PreferencesPage />
          </RouteGuardWrapper>
        ),
      },
      {
        path: 'verification',
        element: (
          <RouteGuardWrapper auth>
            <MyAccountPage />
          </RouteGuardWrapper>
        ),
      },
      {
        path: 'security',
        element: (
          <RouteGuardWrapper auth>
            <SecurityPage />
          </RouteGuardWrapper>
        ),
      },
      {
        path: 'change-password',
        element: <Navigate to='/my-account/security' replace />,
      },
      {
        path: 'messages',
        children: [
          {
            index: true,
            element: <Navigate to='inbox' replace />,
          },
          {
            path: ':tab',
            element: (
              <RouteGuardWrapper auth maintenance={false}>
                <MessagesPage />
              </RouteGuardWrapper>
            ),
          },
        ],
      },
    ],
  },
  {
    path: 'casino',
    element: (
      <RouteGuardWrapper casinoAccess maintenanceProduct='casino'>
        <CasinoPage />
      </RouteGuardWrapper>
    ),
  },
  {
    path: 'casino/game/:gameId',
    element: (
      <RouteGuardWrapper casinoAccess maintenanceProduct='casino'>
        <CasinoGamePage />
      </RouteGuardWrapper>
    ),
  },
  {
    path: '/content/:page',
    element: (
      <RouteGuardWrapper>
        <ContentPage />
      </RouteGuardWrapper>
    ),
  },
  {
    path: '/promotions/:promotionId',
    element: (
      <RouteGuardWrapper requiredGbFeature={FEATURE_FLAG.PROMOTIONS_ENABLED}>
        <PromotionDetailsPage />
      </RouteGuardWrapper>
    ),
  },
  {
    path: '/promotions',
    element: (
      <RouteGuardWrapper requiredGbFeature={FEATURE_FLAG.PROMOTIONS_ENABLED}>
        <PromotionsPage />
      </RouteGuardWrapper>
    ),
  },
  {
    path: 'change-password',
    element: (
      <RouteGuardWrapper auth>
        <ChangePasswordPage />
      </RouteGuardWrapper>
    ),
  },
  ...commonRoutes,
];

const casinoRoutes = [
  {
    index: true,
    element: <Navigate to='./casino' replace />,
  },
  {
    path: 'casino',
    element: (
      <RouteGuardWrapper casinoAccess maintenanceProduct='casino'>
        <StandaloneCasinoPage />
      </RouteGuardWrapper>
    ),
  },
  {
    path: 'casino/game/:gameId',
    element: (
      <RouteGuardWrapper casinoAccess maintenanceProduct='casino'>
        <CasinoGamePage />
      </RouteGuardWrapper>
    ),
  },
  {
    path: '/profile',
    element: (
      <RouteGuardWrapper auth maintenance={false}>
        <ProfilePage />
      </RouteGuardWrapper>
    ),
  },
  {
    path: '/messages',
    children: [
      {
        index: true,
        element: <Navigate to='inbox' replace />,
      },
      {
        path: ':tab',
        element: (
          <RouteGuardWrapper auth maintenance={false}>
            {/* TODO: Temporary reuse of a page as a shell. Unify standalone
                profile/messages with the shared /my-account route layout. */}
            <ProfilePage>
              <MessagesPage />
            </ProfilePage>
          </RouteGuardWrapper>
        ),
      },
    ],
  },
  {
    path: '/promotions/:promotionId',
    element: (
      <RouteGuardWrapper requiredGbFeature={FEATURE_FLAG.PROMOTIONS_ENABLED}>
        <PromotionDetailsPage />
      </RouteGuardWrapper>
    ),
  },
  {
    path: '/promotions',
    element: (
      <RouteGuardWrapper requiredGbFeature={FEATURE_FLAG.PROMOTIONS_ENABLED}>
        <PromotionsPage />
      </RouteGuardWrapper>
    ),
  },
  ...commonRoutes,
];

export const getRoutesConfig = (layoutType?: 'sportsbook' | 'casino') => {
  if (!layoutType) return [];

  const isStandaloneCasino = layoutType === 'casino';

  return isStandaloneCasino ? casinoRoutes : sportsbookRoutes;
};
